import { useState } from "react";
import { siteMeta } from "@/mocks/site";

type Status = "idle" | "submitting" | "success" | "error";

const FORM_ID = "gualian-brief-form";
const SUBMIT_URL = "https://readdy.ai/api/form/dahg9h3oh653ivfvnu70";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，简报会在每天固定时段发送。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查邮箱格式后重试",
        );
        setStatus("error");
        return;
      }

      setStatus("success");
      setMessage("订阅成功。我们会在每天固定时段，把过去 24 小时的热度走势汇总发到你的邮箱。");
      form.reset();
    } catch (error) {
      setFormError("网络异常，请稍后重试");
      setStatus("error");
    }
  };

  return (
    <section id="subscribe" className="bg-background-100 border-t border-background-200 py-16 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="rounded-2xl bg-primary-600 text-background-50 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-6 p-7 md:p-10">
              <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] text-primary-100 uppercase">
                <span className="w-6 h-px bg-primary-200" />
                DAILY BRIEF
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold leading-tight">
                订阅{siteMeta.brand}追踪简报
              </h2>
              <p className="mt-4 text-sm md:text-base text-primary-50/85 leading-relaxed max-w-md">
                每天固定时段，一封邮件汇总过去 24 小时的热度走势：新进榜事件、陡升节点、渠道变化，
                以及值得留意的二次发酵风险。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-primary-50/90">
                {["新进榜事件与首爆时间", "陡升节点与跨平台跳转提示", "长尾事件与二次发酵风险扫描"].map(
                  (item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-5 h-5 flex items-center justify-center text-accent-300">
                        <i className="ri-checkbox-circle-line" />
                      </span>
                      {item}
                    </li>
                  ),
                )}
              </ul>
            </div>

            <div className="lg:col-span-6 bg-background-50 text-foreground-900 p-7 md:p-10">
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
                noValidate={false}
              >
                <div>
                  <label htmlFor="sub-email" className="block text-sm font-label text-foreground-800 mb-2">
                    邮箱地址 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-12 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="sub-nickname" className="block text-sm font-label text-foreground-800 mb-2">
                      称呼
                    </label>
                    <input
                      id="sub-nickname"
                      name="nickname"
                      type="text"
                      placeholder="怎么称呼你"
                      className="w-full h-12 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="sub-focus" className="block text-sm font-label text-foreground-800 mb-2">
                      关注方向
                    </label>
                    <select
                      id="sub-focus"
                      name="focus"
                      defaultValue="全部方向"
                      className="w-full h-12 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                    >
                      <option value="全部方向">全部方向</option>
                      <option value="商业争议">商业争议</option>
                      <option value="带货翻车">带货翻车</option>
                      <option value="人设崩塌">人设崩塌</option>
                      <option value="情感纠纷">情感纠纷</option>
                      <option value="网红言论">网红言论</option>
                    </select>
                  </div>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="topic-ref-aux"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                  </span>
                  {status === "submitting" ? "提交中..." : "立即订阅"}
                </button>

                {status === "success" && message && (
                  <p className="flex items-start gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-checkbox-circle-line" />
                    </span>
                    {message}
                  </p>
                )}
                {status === "error" && formError && (
                  <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-error-warning-line" />
                    </span>
                    {formError}
                  </p>
                )}

                <p className="text-xs text-foreground-500 leading-relaxed">
                  我们仅使用你的邮箱发送追踪简报，可随时通过邮件底部的退订链接取消订阅，不会用于其他用途。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}